/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module CportalModule */

import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    Type,
} from '@angular/core';

import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    IALBServicesStatus,
    ICluster,
} from 'generated-types';

import {
    find,
    take,
    takeUntil,
} from 'rxjs/operators';

import {
    CportalService,
    IError,
    PortalInfo,
} from 'ajs/modules/cportal';

import { ADMIN_TENANT_NAME } from 'ajs/modules/core/core.constants';
import { AsyncFactory } from 'ajs/modules/core/factories/async-factory/async.factory';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { AviBackendErrorMsgPipe } from 'nsx-alb-ui-components';
import { CentralLicenseAlertsService } from 'ng/modules/core/services/central-license-alerts';
import { ClusterItem } from 'items/cluster.item.factory';
import { configUpdateDuration, statusPollingDuration } from 'ajs/modules/cportal/constants';
import { IPromise } from 'angular';
import { IWidgetConfigWithPermissions } from 'ng/modules/dashboard/dashboard.types';
import { L10nService } from '@vmw/ngx-vip';
import { StateService } from '@uirouter/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { waitFor } from 'ajs/js/utilities/timer';
import * as globalL10n from 'global-l10n';
import * as SystemInfoActions from 'ng/root-store/system-info/system-info.actions';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';

import './cportal-page.component.less';
import { CloudWidgetComponent } from './cloud-widget';
import { CPortalPageStore } from './cportal-page.store';
import { ProxyWidgetComponent } from './proxy-widget/proxy-widget.component';
import { SmartOperationsWidgetComponent } from './smart-operations-widget/smart-operations-widget.component';
import { ThreatIntelligenceWidgetComponent } from './threat-intelligence-widget';
import * as l10n from './cportal-page.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TAsyncFactory = typeof AsyncFactory;
type TPortalInfo = typeof PortalInfo;
type TCluster = typeof ClusterItem;

enum AlertType {
    success = 'success',
    danger = 'danger',
    default = 'default',
    info = 'info',
}

/**
 * @description
 *      Component used to represent Cportal page.
 * @author Ram Pal, Navneet Sharma, Rajawant Prajapti, Ratan Kumar, Deepak Chaudhary, Dnyaneshwar Jadhav
 */
@Component({
    selector: 'cportal-page',
    templateUrl: './cportal-page.component.html',
})
export class CPortalPageComponent implements OnInit, OnDestroy {
    /**
     * Default Avi cloud connected status.
     * Used to check if controller is connected with the pulse.
     */
    public aviCloudConnected = false;

    /**
     * Default controller registration status.
     * Used to check if controller is Registered with the pulse.
     */
    public isControllerRegistered = false;

    /**
     * Default variable for error message.
     */
    public error = '';

    /**
     * Error message specific to controller connectivity.
     */
    public connectivityError = '';

    /**
     * Name of existing controller.
     */
    public controllerName = '';

    /**
     * Set to true when controller registration and de-registration is in progress.
     */
    public busy = false;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Get keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Property holding AlertType.
     */
    public readonly AlertType = AlertType;

    /**
     * Hide Alert message on Unregistered button click.
     */
    public showAlertMessage = false;

    /**
     * To indicate whether the tenant is an Admin tenant.
     */
    public isAdminTenant = true;

    public showReconnectBtn = true;

    public readonly portalUrl$ = this.cPortalPageStore.portalUrl$;

    public readonly storeError$ = this.cPortalPageStore.error$;

    /**
     * Cloud Services widgets to render.
     */
    public widgetConfigs: IWidgetConfigWithPermissions[] = [];

    /**
     * Instance of portalInfo Item.
     */
    private portalInfo: PortalInfo = null;

    /**
     * Instance of Cluster Item
     */
    private cluster: ClusterItem = null;

    /**
     * Use to check if component is destroyed
     */
    private isDestroyed = false;

    /**
     * Instance of AsyncFactory when we have an ongoing polling.
     */
    private pollingPulseStatus: AsyncFactory = null;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        private readonly cPortalService: CportalService,
        @Inject('Cluster')
        private readonly Cluster: TCluster,
        @Inject('PortalInfo')
        private readonly PortalInfo: TPortalInfo,
        @Inject('AsyncFactory')
        private readonly AsyncFactory: TAsyncFactory,
        private readonly l10nService: L10nService,
        private readonly aviBackendErrorMsgPipe: AviBackendErrorMsgPipe,
        private readonly store: Store,
        private readonly centralLicenseAlertsService: CentralLicenseAlertsService,
        private readonly authService: Auth,
        private readonly cPortalPageStore: CPortalPageStore,
        private readonly $state: StateService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Get connectivity Status based on aviCloudConnected and connectivityError.
     */
    public get aviCloudConnectedStatus(): string {
        if (!this.aviCloudConnected && this.isUnregisteredAndDisconnected) {
            return this.AlertType.default;
        }

        if (this.aviCloudConnected && !this.busy) {
            return this.AlertType.success;
        } else if (this.hasConnectivityError) {
            return this.AlertType.danger;
        }

        return this.AlertType.default;
    }

    /**
     * Getter will return true if any error occurs.
     */
    public get hasConnectivityError(): boolean {
        return Boolean(this.error || this.connectivityError);
    }

    /**
     * If controller is unregistered and Avi cloud Connected getter will return true.
     */
    public get isUnregisteredAndConnected(): boolean {
        return !this.isControllerRegistered && this.aviCloudConnected;
    }

    /**
     * If controller is unregistered and Avi cloud is disconnected getter will return true.
     */
    public get isUnregisteredAndDisconnected(): boolean {
        return !this.isControllerRegistered && !this.aviCloudConnected;
    }

    public get reconnectNowVisible(): boolean {
        return this.hasConnectivityError && !this.isUnregisteredAndConnected && !this.busy && this.showReconnectBtn;
    }

    /**
     * Getter will return message based on connectivity and registered status.
     */
    public get connectivityAndRegisteredMessage(): string | null {
        const { l10nService } = this;

        if (this.busy && !this.showAlertMessage) {
            return null;
        }

        if (this.busy && this.showAlertMessage) {
            return l10nService.getMessage(l10nKeys.attemptingToConnectMessage);
        }

        if (this.hasConnectivityError && this.isControllerRegistered) {
            return this.aviBackendErrorMsgPipe.transform(this.error) || this.connectivityError;
        }

        if (!this.busy) {
            if (this.isUnregisteredAndConnected) {
                return l10nService.getMessage(l10nKeys.notConnectedErrorMessage);
            }

            if (this.hasConnectivityError && this.isUnregisteredAndDisconnected) {
                return l10nService.getMessage(l10nKeys.notRegisteredAndConnectedErrorMessage);
            }
        }

        return null;
    }

    public async ngOnInit(): Promise<void> {
        this.isAdminTenant = this.authService.getTenantName() === ADMIN_TENANT_NAME;

        this.store.select(SystemInfoSelectors.selectLocalCluster)
            .pipe(take(1))
            .subscribe((cluster: ICluster) => this.controllerName = cluster?.name);

        await this.cPortalPageStore.loadPortalInfo();
        await this.cPortalPageStore.loadLicenseStatus();

        this.cPortalPageStore.albServiceStatus$.subscribe((albServiceStatus: IALBServicesStatus) => {
            if (albServiceStatus) {
                const {
                    registration_status: registrationStatus,
                    connectivity_status: connectivityStatus,
                    error: connectivityError,
                } = albServiceStatus;

                this.isControllerRegistered =
                    registrationStatus === ALBServicesRegistrationStatus.ALBSERVICES_REGISTERED;
                this.aviCloudConnected = connectivityStatus === ALBServicesConnectivityStatus.ALBSERVICES_CONNECTED;
                this.connectivityError = connectivityError ?? '';

                // If connectivity is lost and api does not return any error message
                // then following standard error message will be displayed.
                if (!this.aviCloudConnected && !connectivityError && this.isControllerRegistered) {
                    this.connectivityError = this.l10nService.getMessage(l10nKeys.connectionErrorMessage);
                }
            }
        });

        // Subscribe to store errors from child components
        this.cPortalPageStore
            .error$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((storeError: string) => {
                if (storeError) {
                    this.error = storeError;
                    this.showReconnectBtn = false;
                }
            });

        this.cluster = new this.Cluster({ id: 'default' });
        this.pollingPulseStatus = new this.AsyncFactory(async() => {
            await this.cPortalPageStore.loadConnectivityStatus();

            const albServiceStatus = await this.cPortalPageStore.albServiceStatus$.pipe(take(1)).toPromise();

            if (!albServiceStatus.error) {
                this.hideErrorMessage();
            }

            // Update controller registration status in centralLicenseAlertService and
            // call method to show/hide license subscription failed alert.
            const { registration_status: registrationStatus } = albServiceStatus;

            this.centralLicenseAlertsService.controllerRegistrationStatus = registrationStatus;
            this.centralLicenseAlertsService.setSaasStatusAndRefreshLicenseFailAlert();
        }, {
            maxSkipped: 5,
            callback: () => this.store.dispatch(SystemInfoActions.cancelLoading()),
        });

        this.pollingPulseStatus.start(statusPollingDuration);
        this.setWidgetConfigs();
        this.checkAndOpenRegistrationModal();
        this.cPortalPageStore.clearError();
    }

    /**
     * This function is used to reconnect controller with portal in case
     * when controller is registered but connection is lost.
     */
    public reconnectController(): void {
        this.busy = true;
        this.showAlertMessage = false;

        this.connectAviCloud()
            .then(() => waitFor(configUpdateDuration))
            .then(() => this.updateStatus())
            .catch((error: string) => this.error = error)
            .finally(() => this.busy = false);
    }

    /**
     * Open dialog modal for registration with opt-in features.
     */
    public async openRegistrationModal(editMode = false): Promise<void> {
        const { l10nService } = this;

        this.showAlertMessage = true;

        if (!editMode) {
            if (!this.aviCloudConnected) {
                const errorMessage = l10nService.getMessage(l10nKeys.cloudServicesRegisterControllerMessage);

                return Promise.reject(errorMessage);
            }
        }

        this.portalInfo = new this.PortalInfo();
        await this.portalInfo.load();

        this.portalInfo.edit(null, {
            controllerName: this.controllerName,
            onComplete: () => this.updateCtrlAndRegistrationDetails(),
            reloadStore: () => this.cPortalPageStore.loadPortalInfo(),
            editMode,
        }).catch((error: IError) => Promise.reject(new Error(error.data as string ?? '')));
    }

    /**
     * Hide error message.
     */
    public hideErrorMessage(): void {
        this.error = '';
    }

    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
        this.isDestroyed = true;
        this.cluster.destroy();
        this.cPortalService.clearLoginWindowInterval();

        if (this.portalInfo) {
            this.portalInfo.destroy();
        }

        // Stop polling of pulse status.
        this.pollingPulseStatus.stop();
    }

    /**
     * Function gets the latest registration status.
     */
    private async updateStatus(): Promise<void> {
        if (this.isDestroyed) {
            return;
        }

        await this.cPortalPageStore.loadConnectivityStatus();
    }

    /**
     * Function to authenticate user with Portal.
     */
    private connectAviCloud(): IPromise<void> {
        if (this.aviCloudConnected) {
            // If controller is already connected with portal then promise will be resolved and returned.
            return Promise.resolve();
        } else {
            return this.cPortalService.openLoginWindow();
        }
    }

    /**
     * Update controller and registration details. If pollingTimeout and maxPollingCount
     * params are passed then Pulse connection status is polled after interval defined by
     * pollingTimeout for maximum number of times defined by maxPollingCount.
     * [pollingTimeout=configUpdateDuration] - timeout after which the status
     * will be polled.
     * [maxPollingCount=1] - maximum number of attempts for polling pulse
     * connection status.
     */
    private updateCtrlAndRegistrationDetails(
        pollingTimeout = configUpdateDuration,
        maxPollingCount = 1,
    ): IPromise<void> {
        this.busy = true;

        return waitFor(pollingTimeout)
            .then(() => {
                this.store.dispatch(SystemInfoActions.passiveLoadSystemInfo());

                // TODO: AV-181311
                // replace toPromise() with firstValueFrom() when RxJs v8 is available
                return this.store.select(SystemInfoSelectors.selectLoaded)
                    .pipe(find((loaded: boolean) => Boolean(loaded)))
                    .toPromise();
            })
            .then(() => {
                this.store.select(SystemInfoSelectors.selectLocalCluster)
                    .pipe(take(1))
                    .subscribe((cluster: ICluster) => this.controllerName = cluster?.name);

                return this.updateStatus();
            })
            .then(() => {
                if (!this.aviCloudConnected && maxPollingCount > 1) {
                    return this.updateCtrlAndRegistrationDetails(pollingTimeout, maxPollingCount - 1);
                }
            })
            .finally(() => {
                this.busy = false;
            });
    }

    private setWidgetConfigs(): void {
        this.widgetConfigs = [{
            widget: CloudWidgetComponent as Type<Component>,
            width: 2,
        }, {
            widget: ProxyWidgetComponent as Type<Component>,
        }, {
            widget: SmartOperationsWidgetComponent as Type<Component>,
        }, {
            widget: ThreatIntelligenceWidgetComponent as Type<Component>,
        }];
    }

    /**
     * Check if openRegistrationModal param is set to true to open registration modal.
     * This is used when navigating from licensing page after cloud licensing subscription.
     */
    private checkAndOpenRegistrationModal(): void {
        const { openRegistrationModal } = this.$state.params;

        if (openRegistrationModal) {
            this.openRegistrationModal(true);
        }
    }
}
